import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { getNamespaceId } from '@blaze-runtime/engine-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import { NavMenu } from '@blaze-shared/elements-domain';
import { blazePageActions } from '@blaze-shared/projects-data-access';
import { DialogService } from '@blaze-shared/shared-feature';

import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { LayoutsApiService } from '../../data/layouts-api.service';
import { LayoutLoadingService } from '../../services/layout-loading.service';
import {
  areSectionsPresent,
  findReferenceItemByTargetId,
} from '../../services/nav-menu-items.service';
import { PageGenerationService } from '../../services/page-pregeneration.service';
import {
  addNewElementAtPath,
  ElementsFacade,
  getElementById,
  getLayoutEntities,
} from '../elements';

import {
  addSharedItem,
  changeMenuDirection,
  cleanLastAdded,
  deleteItem,
  initItems,
  itemsLoaded,
  openPropsFloatingPanelAfterAddingItem,
  requestAddSharedItem,
  requestChangeMenuDirection,
  requestDeleteItem,
  requestInitItems,
} from './nav-menu.actions';
import { selectEntities, selectLastAdded } from './nav-menu.selectors';

@Injectable()
export class NavMenuEffects {
  private readonly actions$ = inject(Actions);
  private readonly dialogService = inject(DialogService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly layoutLoading = inject(LayoutLoadingService);
  private readonly layoutsApiService = inject(LayoutsApiService);
  private readonly pageGenerationService = inject(PageGenerationService);
  private readonly store = inject(Store);

  loadNavMenuItemsLayout$ = createEffect(() =>
    combineLatest({
      pages: this.store.select(runtimeSelectors.selectPages),
      loadedLayoutIds: this.layoutLoading.loadedLayoutIds$,
    }).pipe(
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
      filter(([, projectId]) => !!projectId),
      map(([{ pages, loadedLayoutIds }]) => ({
        page: pages.find((page) => page.pageType === 'NavMenus'),
        loadedLayoutIds,
      })),
      filter(({ page, loadedLayoutIds }) => !!page && !loadedLayoutIds.includes(page.id)),
      distinctUntilChanged(),
      concatMap(({ page }) => {
        if (!page) {
          throw new Error('No page found to load nav menu items from');
        }

        return this.layoutLoading.loadAndWait(page.id).pipe(
          concatLatestFrom(() => this.store.select(getLayoutEntities)),
          concatMap(([layoutId, layoutEntities]) => {
            const layout = layoutEntities[layoutId];

            if (!layout) {
              throw new Error(`Nav menu items layout was not loaded! Id: ${layoutId}`);
            }

            const items = (layout.uiLayer.content as unknown as NavMenu.LegacySharedItem[]) ?? [];
            return [itemsLoaded({ items })];
          }),
        );
      }),
    ),
  );

  addNewItemAndCreateStorageWhenNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestAddSharedItem, requestInitItems),
      concatLatestFrom(() => this.store.select(fromRouter.getProjectId)),
      concatMap(([action, projectId]) => {
        return this.store.select(runtimeSelectors.selectPages).pipe(
          take(1),
          switchMap((loadedPages) => {
            const currentStoragePage = loadedPages.find((page) => page.pageType === 'NavMenus');

            if (currentStoragePage) {
              return of({ action, storagePage: currentStoragePage });
            }

            return this.pageGenerationService
              .createPage(
                {
                  isHomePage: false,
                  name: 'NavMenus',
                  pageType: 'NavMenus',
                  projectId,
                },
                loadedPages,
              )
              .pipe(
                switchMap((page) =>
                  this.layoutsApiService
                    .updateLayouts([
                      {
                        pageId: page.id,
                        uiLayer: { content: [], properties: { type: 'NavMenuItems' } },
                        actionLayer: { connections: [] },
                        version: 0,
                      },
                    ])
                    .pipe(
                      switchMap(() =>
                        this.layoutLoading.loadAndWait(page.id).pipe(map(() => page)),
                      ),
                      tap(() => {
                        this.store.dispatch(blazePageActions.addOrUpdatePage({ page }));
                      }),
                    ),
                ),
                switchMap(() => this.store.select(runtimeSelectors.selectPages)),
                map((pages) => pages.find((page) => page.pageType === 'NavMenus')),
                first((createdStoragePage) => !!createdStoragePage),
                catchError((error) => {
                  console.error('Error creating storage page for nav menu items', error);
                  return of(null);
                }),
                map((createdStoragePage) => ({ action, storagePage: createdStoragePage })),
              );
          }),
        );
      }),
      concatMap(({ action: { type, placement }, storagePage }) => {
        if (!storagePage) {
          return EMPTY;
        }

        return type === requestInitItems.type
          ? [initItems({ namespaceId: storagePage.id, placement })]
          : [addSharedItem({ namespaceId: storagePage.id, placement })];
      }),
    ),
  );

  confirmDeleteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestDeleteItem),
      concatLatestFrom(() => [this.elementsFacade.elements$, this.store.select(selectEntities)]),
      concatMap(([{ id, elementId }, elements, menuEntities]) => {
        const isReferencedByOtherElements = elements.some((element) => {
          if (!isElementType(element, 'navMenu') || element.id === elementId) {
            return false;
          }
          const storage = element.props.content?.storage;
          if (!storage) {
            return false;
          }
          return !!findReferenceItemByTargetId(id, storage);
        });

        const isSharedSubMenuItem = menuEntities[id] && !!menuEntities[id].parentId;
        const deleteAction = deleteItem({ id });

        if (isReferencedByOtherElements || isSharedSubMenuItem) {
          return this.dialogService
            .openConfirmDialog({
              title: 'Delete Confirmation',
              description:
                'This menu item is used in other navigation menu component. Deleting it here will remove it from all menus where it has been added.',
              cancelLabel: 'Cancel',
              confirmLabel: 'Yes, delete',
            })
            .afterClosed()
            .pipe(concatMap((confirmed) => (confirmed ? [deleteAction] : EMPTY)));
        }

        return [deleteAction];
      }),
    ),
  );

  initializeMenuItemsForNewNavMenu$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addNewElementAtPath),
      filter(({ elementDefinition }) => elementDefinition.type === 'navMenu'),
      concatLatestFrom(() => this.elementsFacade.elements$),
      concatMap(([{ atPath }, elements]) => {
        const namespaceId = getNamespaceId(atPath?.id ?? '');
        if (!namespaceId) {
          return EMPTY;
        }

        const element = elements.find(
          (el) =>
            el.ns === namespaceId &&
            isElementType(el, 'navMenu') &&
            el.props.content.storage === undefined,
        );

        if (!element) {
          return EMPTY;
        }

        return [requestInitItems({ placement: { elementId: element.id } })];
      }),
    ),
  );

  openEditDialogForAddedMenuItem = createEffect(() =>
    this.actions$.pipe(
      ofType(addSharedItem),
      concatLatestFrom(({ placement: { elementId } }) => [
        this.store.select(getElementById(elementId)),
        this.store.select(selectLastAdded),
      ]),

      concatMap(([, element, lastAdded]) => {
        if (!isElementType(element, 'navMenu') || !lastAdded) {
          console.warn('Element or lastAdded not found.');
          return [cleanLastAdded()];
        }

        const storage = element.props.content.storage;
        if (!storage) {
          console.warn('Element does not have menu items.');
          return [cleanLastAdded()];
        }

        return [
          openPropsFloatingPanelAfterAddingItem({
            elementId: element.id,
            itemId: lastAdded.subItemId ?? lastAdded.itemId ?? '',
          }),
          cleanLastAdded(),
        ];
      }),
    ),
  );

  changeMenuDirection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestChangeMenuDirection),
      concatLatestFrom(({ elementId }) => this.store.select(getElementById(elementId))),
      map(([{ direction }, element]) => ({ direction, element })),
      concatMap(({ direction, element }) => {
        if (!isElementType(element, 'navMenu')) {
          return EMPTY;
        }

        const storage = element.props.content.storage;
        if (!storage || direction === 'vertical') {
          return [changeMenuDirection({ direction, elementId: element.id })];
        }

        if (!areSectionsPresent(storage)) {
          return [changeMenuDirection({ direction, elementId: element.id })];
        }

        return this.dialogService
          .openConfirmDialog({
            title: 'Change direction warning',
            description: `This menu contains sections which are supported only is vertical direction. Changing it to ${direction} will <b>remove</b> all sections. Do you want to continue?`,
            cancelLabel: 'Cancel',
            confirmLabel: 'Yes, change direction',
          })
          .afterClosed()
          .pipe(
            concatMap((confirmed) =>
              confirmed ? [changeMenuDirection({ direction, elementId: element.id })] : EMPTY,
            ),
          );
      }),
    ),
  );
}
